import React from "react";

// 依存を増やさない軽量Markdownレンダラ（日報プレビュー用）。
// React要素として組み立てるため、本文テキストは自動エスケープされ XSS 安全。
// 対応記法: 見出し(#..######) / 箇条書き(- *／入れ子可) / 番号付き(1.) /
//           チェックリスト(- [ ] - [x]) / 引用(>) / 水平線(--- *** ___) /
//           コードブロック(```) / 表(| A | B |) / 段落・改行 と、
//           インライン: 太字(**), 斜体(*), 取り消し線(~~), コード(`), リンク([t](url))。
// 未対応（意図的）: HTML直書き（XSS対策）、画像、脚注。

// リンク先は http(s)/mailto のみ許可（javascript: 等は無効化）。
function safeHref(url) {
  return /^(https?:|mailto:)/i.test(url) ? url : undefined;
}

// インライン記法をReactノード配列へ。earliest-match で順に切り出す。
function parseInline(text, kp) {
  const nodes = [];
  let rest = text;
  let k = 0;
  const re = /(`[^`]+`)|(\*\*[^*]+?\*\*)|(~~[^~]+?~~)|(\*[^*]+?\*)|(\[[^\]]+\]\([^)]+\))/;
  while (rest) {
    const m = re.exec(rest);
    if (!m) { nodes.push(rest); break; }
    if (m.index > 0) nodes.push(rest.slice(0, m.index));
    const tok = m[0];
    const key = `${kp}-i${k++}`;
    if (tok.startsWith("`")) {
      nodes.push(<code key={key}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith("**")) {
      nodes.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("~~")) {
      nodes.push(<del key={key}>{tok.slice(2, -2)}</del>);
    } else if (tok.startsWith("*")) {
      nodes.push(<em key={key}>{tok.slice(1, -1)}</em>);
    } else {
      const lm = /\[([^\]]+)\]\(([^)]+)\)/.exec(tok);
      const href = safeHref(lm[2]);
      nodes.push(href
        ? <a key={key} href={href} target="_blank" rel="noopener noreferrer">{lm[1]}</a>
        : lm[1]);
    }
    rest = rest.slice(m.index + tok.length);
  }
  return nodes;
}

// 段落内は各行を <br/> でつなぐ（1改行=改行として扱う＝日報向け）。
function paragraphNodes(lines, kp) {
  const out = [];
  lines.forEach((ln, idx) => {
    if (idx > 0) out.push(<br key={`${kp}-br${idx}`} />);
    parseInline(ln, `${kp}-l${idx}`).forEach((n) => out.push(n));
  });
  return out;
}

// ───────────── リスト（箇条書き／番号付き／チェックリスト・入れ子対応） ─────────────

const LIST_RE = /^(\s*)([-*]|\d+\.)\s+(.*)$/;
const TASK_RE = /^\[([ xX])\]\s+(.*)$/; // - [ ] / - [x]

// リスト行を {indent, ordered, text} に。タブは半角4つ分として数える。
function listItemOf(line) {
  const m = LIST_RE.exec(line);
  if (!m) return null;
  return {
    indent: m[1].replace(/\t/g, "    ").length,
    ordered: /\d/.test(m[2]),
    text: m[3],
  };
}

// li 1個。チェックリストは読み取り専用のチェックボックスにする（日報は表示専用）。
function listItemNode(item, kids, key) {
  const t = TASK_RE.exec(item.text);
  if (t) {
    return (
      <li key={key} className="mp-md-task">
        <input type="checkbox" checked={t[1] !== " "} disabled readOnly />
        {parseInline(t[2], key)}
        {kids}
      </li>
    );
  }
  return <li key={key}>{parseInline(item.text, key)}{kids}</li>;
}

// items[start] から同じインデント幅のリストを1つ組み立て、[要素, 次の位置] を返す。
// より深いインデントの行は直前の項目の子リストとして再帰的に取り込む。
function buildList(items, start, kp) {
  const indent = items[start].indent;
  const ordered = items[start].ordered;
  const lis = [];
  let i = start;
  let n = 0;
  while (i < items.length && items[i].indent >= indent) {
    const item = items[i];
    const key = `${kp}-${n++}`;
    i++;
    const kids = [];
    while (i < items.length && items[i].indent > indent) {
      const [child, next] = buildList(items, i, `${key}s${kids.length}`);
      kids.push(child);
      i = next;
    }
    lis.push(listItemNode(item, kids, key));
  }
  return [ordered ? <ol key={kp}>{lis}</ol> : <ul key={kp}>{lis}</ul>, i];
}

// ───────────── 表（GFM形式） ─────────────

// 行を セル配列 へ。前後の | を落とし、\| はセル内の | として扱う。
function splitRow(line) {
  const s = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  const cells = [];
  let cur = "";
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\" && s[i + 1] === "|") { cur += "|"; i++; continue; }
    if (s[i] === "|") { cells.push(cur); cur = ""; continue; }
    cur += s[i];
  }
  cells.push(cur);
  return cells.map((c) => c.trim());
}

// 区切り行（| --- | :---: | など）か。
function isTableSep(line) {
  if (!line.includes("|")) return false;
  const cells = splitRow(line);
  return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c));
}

// 表の開始行か（見出し行＋次行が区切り行）。
function isTableStart(lines, i) {
  return lines[i].includes("|") && i + 1 < lines.length && isTableSep(lines[i + 1]);
}

// 区切り行のセルから寄せを決める（:--- 左 / :---: 中央 / ---: 右）。
function alignStyle(cell) {
  const l = cell.startsWith(":");
  const r = cell.endsWith(":");
  if (l && r) return { textAlign: "center" };
  if (l) return { textAlign: "left" };
  if (r) return { textAlign: "right" };
  return undefined;
}

// 表を組み立て、[要素, 次の位置] を返す。スマホではみ出さないよう横スクロール枠で包む。
function buildTable(lines, start, key) {
  const head = splitRow(lines[start]);
  const aligns = splitRow(lines[start + 1]).map(alignStyle);
  let i = start + 2;
  const rows = [];
  while (i < lines.length && lines[i].includes("|") && !/^\s*$/.test(lines[i])) {
    const cells = splitRow(lines[i]);
    // 列数がずれても崩れないよう見出しの列数に揃える。
    rows.push(head.map((_, n) => (n < cells.length ? cells[n] : "")));
    i++;
  }
  const el = (
    <div className="mp-md-table" key={key}>
      <table>
        <thead>
          <tr>{head.map((c, n) => <th key={`${key}-h${n}`} style={aligns[n]}>{parseInline(c, `${key}-h${n}`)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((cells, r) => (
            <tr key={`${key}-r${r}`}>
              {cells.map((c, n) => <td key={`${key}-r${r}c${n}`} style={aligns[n]}>{parseInline(c, `${key}-r${r}c${n}`)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return [el, i];
}

// ───────────── ブロック ─────────────

const HR_RE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;

// 段落を打ち切るべき行か（他のブロックの開始行）。
function isBlockStart(lines, i) {
  const l = lines[i];
  return /^(#{1,6})\s/.test(l) || /^```/.test(l) || /^>\s?/.test(l)
    || HR_RE.test(l) || LIST_RE.test(l) || isTableStart(lines, i);
}

function render(text) {
  const lines = String(text).replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  let b = 0;
  while (i < lines.length) {
    const line = lines[i];
    const key = `b${b++}`;

    // コードブロック ```
    if (/^```/.test(line)) {
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++; // 終端の ``` を消費
      blocks.push(<pre key={key}><code>{buf.join("\n")}</code></pre>);
      continue;
    }
    // 見出し
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const Tag = `h${h[1].length}`;
      blocks.push(<Tag key={key}>{parseInline(h[2], key)}</Tag>);
      i++;
      continue;
    }
    // 水平線
    if (HR_RE.test(line)) { blocks.push(<hr key={key} />); i++; continue; }
    // 引用
    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\s?/, "")); i++; }
      blocks.push(<blockquote key={key}>{paragraphNodes(buf, key)}</blockquote>);
      continue;
    }
    // 表
    if (isTableStart(lines, i)) {
      const [el, next] = buildTable(lines, i, key);
      blocks.push(el);
      i = next;
      continue;
    }
    // リスト（箇条書き・番号付き・チェックリスト。インデントで入れ子）
    if (LIST_RE.test(line)) {
      const items = [];
      while (i < lines.length && LIST_RE.test(lines[i])) { items.push(listItemOf(lines[i])); i++; }
      let p = 0;
      let n = 0;
      // 先頭より浅い項目が来たら別のリストとして続きを組み立てる。
      while (p < items.length) {
        const [el, next] = buildList(items, p, `${key}-${n++}`);
        blocks.push(el);
        p = next;
      }
      continue;
    }
    // 空行
    if (/^\s*$/.test(line)) { i++; continue; }
    // 段落（連続する通常行）
    const para = [];
    while (i < lines.length && !/^\s*$/.test(lines[i]) && !isBlockStart(lines, i)) {
      para.push(lines[i]); i++;
    }
    blocks.push(<p key={key}>{paragraphNodes(para, key)}</p>);
  }
  return blocks;
}

// text をMarkdownとして描画。空なら null。
function Markdown({ text }) {
  if (!text) return null;
  return <div className="mp-md">{render(text)}</div>;
}

export default Markdown;
